import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  MenuItem,
  Alert,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  Tabs,
  Tab,
  Chip,
  Tooltip,
} from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { nanoid } from 'nanoid'
import Parse from 'parse'
import {
  fetchProductUpdates,
  saveProductUpdates,
} from '../../../../services/productUpdatesService'
import { trackEvent } from '../../../../services/analyticsService'
import { getServerBaseUrl } from '../../../../utils/serverUrl'
import { SEVV_TABS_OVERFLOW_SX } from '../../../../theme/sevvTabsOverflowSx'
import { AdminProductMarkdownField } from './AdminProductMarkdownField'
import {
  duplicateRoadmapItemAsNew,
  pickTopVotedReportItem,
} from '../../../../utils/productUpdatesAdminHelpers'
import type {
  ProductRoadmapItem,
  ProductUpdateHighlight,
  ProductUpdateRelease,
  ProductUpdatesPayload,
  ProductVoteCampaign,
  RoadmapItemStatus,
  RoadmapVoteReportResponse,
} from '../../../../../../types/ProductUpdates'

const FONT = "'Inter', 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif"

const HIGHLIGHT_KINDS = ['feature', 'fix', 'improvement'] as const
const ROADMAP_STATUSES: RoadmapItemStatus[] = ['in_progress', 'planned', 'researching', 'shipped']

const TAB_PREFIX = 'product-updates'
const VOTE_DETAIL_REGION_ID = `${TAB_PREFIX}-vote-detail-region`

function formatHighlightKindLabel(kind: string): string {
  return kind.charAt(0).toUpperCase() + kind.slice(1)
}

function formatRoadmapStatusLabel(status: RoadmapItemStatus): string {
  return status
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

function roadmapStatusChipColor(
  status: RoadmapItemStatus
): 'default' | 'primary' | 'success' | 'info' | 'warning' {
  switch (status) {
    case 'shipped':
      return 'success'
    case 'in_progress':
      return 'primary'
    case 'planned':
      return 'info'
    case 'researching':
      return 'warning'
    default:
      return 'default'
  }
}

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`${TAB_PREFIX}-tabpanel-${index}`}
      aria-labelledby={`${TAB_PREFIX}-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  )
}

function a11yProps(index: number) {
  return {
    id: `${TAB_PREFIX}-tab-${index}`,
    'aria-controls': `${TAB_PREFIX}-tabpanel-${index}`,
  }
}

function emptyPayload(): ProductUpdatesPayload {
  return { schemaVersion: 2, releases: [], roadmap: [], voteCampaigns: [] }
}

function normalizePayload(p: ProductUpdatesPayload): ProductUpdatesPayload {
  return {
    ...p,
    schemaVersion: 2,
    voteCampaigns: Array.isArray(p.voteCampaigns) ? p.voteCampaigns : [],
  }
}

function isoToDatetimeLocalValue(iso: string): string {
  const d = new Date(iso)
  if (!Number.isFinite(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function datetimeLocalToIso(local: string): string {
  const t = Date.parse(local)
  if (!Number.isFinite(t)) return new Date().toISOString()
  return new Date(t).toISOString()
}

export interface AdminProductUpdatesPanelProps {
  onBack: () => void
}

export default function AdminProductUpdatesPanel({ onBack }: AdminProductUpdatesPanelProps) {
  const theme = useTheme()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saveOk, setSaveOk] = useState(false)
  const [payload, setPayload] = useState<ProductUpdatesPayload>(emptyPayload)
  const [payloadVersion, setPayloadVersion] = useState<number | null>(null)
  const [voteReport, setVoteReport] = useState<RoadmapVoteReportResponse | null>(null)
  const [voteReportLoading, setVoteReportLoading] = useState(false)
  const [voteReportError, setVoteReportError] = useState<string | null>(null)
  const [voteDetailOpen, setVoteDetailOpen] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  const [promoteFeedback, setPromoteFeedback] = useState<string | null>(null)

  const roadmapById = useMemo(
    () => new Map(payload.roadmap.map((m) => [m.id, m])),
    [payload.roadmap]
  )

  const topVotedReportRow = useMemo(
    () => (voteReport ? pickTopVotedReportItem(voteReport.items) : null),
    [voteReport]
  )

  const canPromoteTopVoted = Boolean(
    topVotedReportRow &&
      topVotedReportRow.voteCount > 0 &&
      roadmapById.has(topVotedReportRow.id)
  )

  const load = useCallback(async () => {
    setLoading(true)
    setLoadError(null)
    setSaveOk(false)
    try {
      const res = await fetchProductUpdates()
      setPayload(normalizePayload(res.payload || emptyPayload()))
      setPayloadVersion(res.payloadVersion ?? 0)
    } catch (e: unknown) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load')
      setPayload(emptyPayload())
      setPayloadVersion(0)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const loadVoteReport = useCallback(async () => {
    setVoteReportLoading(true)
    setVoteReportError(null)
    try {
      const user = Parse.User.current()
      const token = user?.getSessionToken?.()
      if (!token) {
        throw new Error('You must be signed in to load the vote report.')
      }
      const res = await fetch(`${getServerBaseUrl()}/api/admin/roadmap-vote-report`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = (await res.json().catch(() => ({}))) as RoadmapVoteReportResponse & {
        error?: string
      }
      if (!res.ok) {
        throw new Error(data.error || res.statusText || 'Failed to load vote report')
      }
      if (data.success !== true) {
        throw new Error('Invalid response from server')
      }
      setVoteReport(data)
      trackEvent('APP_product_updates_vote_report_loaded', {
        itemCount: data.items?.length ?? 0,
        voteRowCount: data.votes?.length ?? 0,
      })
    } catch (e: unknown) {
      setVoteReportError(e instanceof Error ? e.message : 'Failed to load vote report')
      setVoteReport(null)
    } finally {
      setVoteReportLoading(false)
    }
  }, [])

  const promoteTopVotedToNewRoadmapItem = useCallback(() => {
    if (!voteReport) return
    const top = pickTopVotedReportItem(voteReport.items)
    if (!top) {
      setPromoteFeedback('No votes recorded yet.')
      return
    }
    const source = roadmapById.get(top.id)
    if (!source) {
      setPromoteFeedback(
        'That item is not in your current roadmap draft — reload the page or add the row manually.'
      )
      return
    }
    const newItem = duplicateRoadmapItemAsNew(source, nanoid(10), {
      titlePrefix: '[Prioritized] ',
      status: 'planned',
    })
    setPayload((p) => ({ ...p, roadmap: [...p.roadmap, newItem] }))
    setPromoteFeedback(`Added roadmap row "${newItem.title}". Save to persist.`)
    setActiveTab(2)
    trackEvent('APP_product_updates_admin_promote_top_vote', {
      sourceId: top.id,
      voteCount: top.voteCount,
    })
  }, [voteReport, roadmapById])

  const duplicateVoteReportRowToRoadmap = useCallback(
    (rowId: string) => {
      const source = roadmapById.get(rowId)
      if (!source) {
        setPromoteFeedback('That row is not in your current roadmap draft — reload the page.')
        return
      }
      const newItem = duplicateRoadmapItemAsNew(source, nanoid(10), { status: 'planned' })
      setPayload((p) => ({ ...p, roadmap: [...p.roadmap, newItem] }))
      setPromoteFeedback(`Duplicated "${source.title}" as a new roadmap row. Save to persist.`)
      setActiveTab(2)
      trackEvent('APP_product_updates_admin_duplicate_from_vote_report', { sourceId: rowId })
    },
    [roadmapById]
  )

  const handleSave = async () => {
    if (payloadVersion === null) return
    setSaving(true)
    setSaveError(null)
    setSaveOk(false)
    try {
      const res = await saveProductUpdates(payload, payloadVersion)
      setPayloadVersion(res.payloadVersion)
      trackEvent('APP_product_updates_saved', { payloadVersion: res.payloadVersion })
      setSaveOk(true)
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Save failed'
      setSaveError(msg)
    } finally {
      setSaving(false)
    }
  }

  const addRelease = () => {
    const r: ProductUpdateRelease = {
      id: nanoid(10),
      date: new Date().toISOString().slice(0, 10),
      title: '',
      highlights: [{ kind: 'feature', text: '' }],
    }
    setPayload((p) => ({ ...p, releases: [r, ...p.releases] }))
  }

  const updateRelease = (index: number, patch: Partial<ProductUpdateRelease>) => {
    setPayload((p) => {
      const releases = [...p.releases]
      releases[index] = { ...releases[index], ...patch }
      return { ...p, releases }
    })
  }

  const removeRelease = (index: number) => {
    setPayload((p) => ({ ...p, releases: p.releases.filter((_, i) => i !== index) }))
  }

  const moveRelease = (index: number, dir: -1 | 1) => {
    const j = index + dir
    if (j < 0 || j >= payload.releases.length) return
    setPayload((p) => {
      const releases = [...p.releases]
      ;[releases[index], releases[j]] = [releases[j], releases[index]]
      return { ...p, releases }
    })
  }

  const addHighlight = (releaseIndex: number) => {
    setPayload((p) => {
      const releases = [...p.releases]
      const hi: ProductUpdateHighlight = { kind: 'feature', text: '' }
      releases[releaseIndex] = {
        ...releases[releaseIndex],
        highlights: [...releases[releaseIndex].highlights, hi],
      }
      return { ...p, releases }
    })
  }

  const updateHighlight = (
    releaseIndex: number,
    hIndex: number,
    patch: Partial<ProductUpdateHighlight>
  ) => {
    setPayload((p) => {
      const releases = [...p.releases]
      const highlights = [...releases[releaseIndex].highlights]
      highlights[hIndex] = { ...highlights[hIndex], ...patch }
      releases[releaseIndex] = { ...releases[releaseIndex], highlights }
      return { ...p, releases }
    })
  }

  const removeHighlight = (releaseIndex: number, hIndex: number) => {
    setPayload((p) => {
      const releases = [...p.releases]
      const highlights = releases[releaseIndex].highlights.filter((_, i) => i !== hIndex)
      releases[releaseIndex] = { ...releases[releaseIndex], highlights }
      return { ...p, releases }
    })
  }

  const moveHighlight = (releaseIndex: number, hIndex: number, dir: -1 | 1) => {
    const j = hIndex + dir
    const len = payload.releases[releaseIndex]?.highlights.length ?? 0
    if (j < 0 || j >= len) return
    setPayload((p) => {
      const releases = [...p.releases]
      const highlights = [...releases[releaseIndex].highlights]
      ;[highlights[hIndex], highlights[j]] = [highlights[j], highlights[hIndex]]
      releases[releaseIndex] = { ...releases[releaseIndex], highlights }
      return { ...p, releases }
    })
  }

  const addRoadmap = () => {
    const m: ProductRoadmapItem = {
      id: nanoid(10),
      title: '',
      summary: '',
      status: 'planned',
    }
    setPayload((p) => ({ ...p, roadmap: [...p.roadmap, m] }))
  }

  const updateRoadmap = (index: number, patch: Partial<ProductRoadmapItem>) => {
    setPayload((p) => {
      const roadmap = [...p.roadmap]
      roadmap[index] = { ...roadmap[index], ...patch }
      return { ...p, roadmap }
    })
  }

  const removeRoadmap = (index: number) => {
    setPayload((p) => ({ ...p, roadmap: p.roadmap.filter((_, i) => i !== index) }))
  }

  const moveRoadmap = (index: number, dir: -1 | 1) => {
    const j = index + dir
    if (j < 0 || j >= payload.roadmap.length) return
    setPayload((p) => {
      const roadmap = [...p.roadmap]
      ;[roadmap[index], roadmap[j]] = [roadmap[j], roadmap[index]]
      return { ...p, roadmap }
    })
  }

  const voteCampaigns = payload.voteCampaigns ?? []

  const addVoteCampaign = () => {
    const closes = new Date(Date.now() + 7 * 86400000).toISOString()
    const c: ProductVoteCampaign = {
      id: nanoid(10),
      closesAt: closes,
      title: '',
    }
    setPayload((p) => ({
      ...p,
      voteCampaigns: [...(p.voteCampaigns ?? []), c],
    }))
  }

  const updateVoteCampaign = (index: number, patch: Partial<ProductVoteCampaign>) => {
    setPayload((p) => {
      const list = [...(p.voteCampaigns ?? [])]
      list[index] = { ...list[index], ...patch }
      return { ...p, voteCampaigns: list }
    })
  }

  const removeVoteCampaign = (index: number) => {
    const id = voteCampaigns[index]?.id
    if (!id) return
    setPayload((p) => ({
      ...p,
      voteCampaigns: (p.voteCampaigns ?? []).filter((_, i) => i !== index),
      roadmap: p.roadmap.map((m) =>
        m.voteCampaignId === id ? { ...m, voteCampaignId: undefined } : m
      ),
    }))
  }

  const moveVoteCampaign = (index: number, dir: -1 | 1) => {
    const j = index + dir
    if (j < 0 || j >= voteCampaigns.length) return
    setPayload((p) => {
      const list = [...(p.voteCampaigns ?? [])]
      ;[list[index], list[j]] = [list[j], list[index]]
      return { ...p, voteCampaigns: list }
    })
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress color="primary" aria-label="Loading" />
      </Box>
    )
  }

  const primary = theme.palette.primary.main
  const saveButtonSx = {
    background: `linear-gradient(135deg, ${primary} 0%, ${theme.palette.primary.light} 100%)`,
    color: theme.palette.primary.contrastText,
    fontWeight: 700,
    textTransform: 'none' as const,
    boxShadow: `0 4px 14px 0 ${alpha(primary, 0.3)}`,
    '&:hover': {
      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${primary} 100%)`,
      boxShadow: `0 6px 18px 0 ${alpha(primary, 0.35)}`,
    },
  }

  return (
    <Box sx={{ fontFamily: FONT }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton
          onClick={onBack}
          aria-label="Back to admin menu"
          sx={{
            '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) },
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography component="h1" variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
          Product updates
        </Typography>
      </Box>

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}
      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
          {saveError}
        </Alert>
      )}
      {saveOk && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaveOk(false)}>
          Saved successfully.
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Newest releases should be first in the list. IDs must be unique (letters, numbers, dots, dashes,
        underscores). Version {payloadVersion ?? '—'} — if save fails with a conflict, reload this page.
      </Typography>

      <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }}>
        <Button variant="contained" onClick={handleSave} disabled={saving} sx={saveButtonSx}>
          {saving ? 'Saving…' : 'Save to database'}
        </Button>
        <Button variant="outlined" onClick={() => void load()} disabled={saving}>
          Reload
        </Button>
      </Stack>

      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          overflow: 'hidden',
          mb: 0,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          aria-label="Product updates sections"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            ...SEVV_TABS_OVERFLOW_SX,
            '& .MuiTab-root': {
              color: 'text.secondary',
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '0.95rem',
              fontFamily: FONT,
              padding: '12px 16px',
              minWidth: 'auto',
              whiteSpace: 'nowrap',
              '&.Mui-selected': {
                color: 'primary.main',
                fontWeight: 700,
              },
              '&:hover': {
                color: 'primary.main',
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: 'primary.main',
              height: 3,
              borderRadius: '3px 3px 0 0',
            },
          }}
        >
          <Tab label="Releases" {...a11yProps(0)} />
          <Tab label="Vote campaigns" {...a11yProps(1)} />
          <Tab label="Roadmap" {...a11yProps(2)} />
          <Tab label="Vote results" {...a11yProps(3)} />
        </Tabs>
      </Box>

      <TabPanel value={activeTab} index={0}>
        <Typography component="h2" variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          Releases
        </Typography>
        <Button startIcon={<AddIcon />} onClick={addRelease} sx={{ mb: 2, textTransform: 'none' }}>
          Add release
        </Button>
        {payload.releases.length === 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            No releases yet. Add a release to show changelog entries in the app.
          </Alert>
        ) : null}
        <Stack spacing={2}>
          {payload.releases.map((rel, ri) => (
            <Paper key={rel.id} variant="outlined" sx={{ p: 2, borderRadius: '12px' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Release #{ri + 1}
                </Typography>
                <Box>
                  <IconButton
                    size="small"
                    aria-label={`Move release ${ri + 1} up in list`}
                    onClick={() => moveRelease(ri, -1)}
                  >
                    <KeyboardArrowUpIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label={`Move release ${ri + 1} down in list`}
                    onClick={() => moveRelease(ri, 1)}
                  >
                    <KeyboardArrowDownIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    aria-label={`Remove release ${ri + 1}`}
                    onClick={() => removeRelease(ri)}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Box>
              </Stack>
              <Stack spacing={1.5}>
                <TextField
                  size="small"
                  label="Id"
                  value={rel.id}
                  onChange={(e) => updateRelease(ri, { id: e.target.value })}
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Date (YYYY-MM-DD)"
                  value={rel.date}
                  onChange={(e) => updateRelease(ri, { date: e.target.value })}
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Title"
                  value={rel.title}
                  onChange={(e) => updateRelease(ri, { title: e.target.value })}
                  fullWidth
                  helperText="Markdown supported in What’s new (e.g. **bold**, links)."
                />
                <TextField
                  size="small"
                  label="Image URL (optional)"
                  value={rel.imageUrl || ''}
                  onChange={(e) => updateRelease(ri, { imageUrl: e.target.value || undefined })}
                  fullWidth
                />
                <Typography variant="caption" fontWeight={600}>
                  Highlights
                </Typography>
                {rel.highlights.map((h, hi) => (
                  <Stack
                    key={hi}
                    spacing={1.25}
                    sx={{
                      pt: hi > 0 ? 1.5 : 0,
                      mt: hi > 0 ? 1.5 : 0,
                      borderTop: hi > 0 ? 1 : 0,
                      borderColor: 'divider',
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                      <TextField
                        select
                        size="small"
                        label="Kind"
                        value={h.kind}
                        onChange={(e) =>
                          updateHighlight(ri, hi, { kind: e.target.value as ProductUpdateHighlight['kind'] })
                        }
                        sx={{ minWidth: 160 }}
                      >
                        {HIGHLIGHT_KINDS.map((k) => (
                          <MenuItem key={k} value={k}>
                            {formatHighlightKindLabel(k)}
                          </MenuItem>
                        ))}
                      </TextField>
                      <Box sx={{ display: 'flex', flexShrink: 0, alignItems: 'center' }}>
                        <IconButton
                          size="small"
                          aria-label={`Move highlight ${hi + 1} up in release ${ri + 1}`}
                          disabled={hi === 0}
                          onClick={() => moveHighlight(ri, hi, -1)}
                        >
                          <KeyboardArrowUpIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label={`Move highlight ${hi + 1} down in release ${ri + 1}`}
                          disabled={hi === rel.highlights.length - 1}
                          onClick={() => moveHighlight(ri, hi, 1)}
                        >
                          <KeyboardArrowDownIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => removeHighlight(ri, hi)}
                          aria-label={`Remove highlight ${hi + 1} from release ${ri + 1}`}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Stack>
                    <AdminProductMarkdownField
                      label="Highlight body"
                      value={h.text}
                      onChange={(t) => updateHighlight(ri, hi, { text: t })}
                      height={168}
                      helperText="Markdown; shown next to the kind chip in What’s new."
                      aria-label={`Release ${ri + 1} highlight ${hi + 1} markdown`}
                    />
                  </Stack>
                ))}
                <Button size="small" onClick={() => addHighlight(ri)} startIcon={<AddIcon />}>
                  Add highlight
                </Button>
              </Stack>
            </Paper>
          ))}
        </Stack>
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <Typography component="h2" variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          Vote campaigns
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Assign a campaign to roadmap items to collect votes until the deadline. Users get one vote per item.
        </Typography>
        <Button startIcon={<AddIcon />} onClick={addVoteCampaign} sx={{ mb: 2, textTransform: 'none' }}>
          Add vote campaign
        </Button>
        {voteCampaigns.length === 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            No vote campaigns. Add one to attach deadlines to roadmap voting.
          </Alert>
        ) : null}
        <Stack spacing={2}>
          {voteCampaigns.map((camp, ci) => (
            <Paper key={camp.id} variant="outlined" sx={{ p: 2, borderRadius: '12px' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Campaign #{ci + 1}
                </Typography>
                <Box>
                  <IconButton
                    size="small"
                    aria-label={`Move campaign ${ci + 1} up in list`}
                    onClick={() => moveVoteCampaign(ci, -1)}
                  >
                    <KeyboardArrowUpIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label={`Move campaign ${ci + 1} down in list`}
                    onClick={() => moveVoteCampaign(ci, 1)}
                  >
                    <KeyboardArrowDownIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    aria-label={`Remove campaign ${ci + 1}`}
                    onClick={() => removeVoteCampaign(ci)}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Box>
              </Stack>
              <Stack spacing={1.5}>
                <TextField
                  size="small"
                  label="Campaign id"
                  value={camp.id}
                  onChange={(e) => updateVoteCampaign(ci, { id: e.target.value.trim() })}
                  fullWidth
                  helperText="Letters, numbers, dots, dashes, underscores"
                />
                <TextField
                  size="small"
                  label="Title (optional)"
                  value={camp.title || ''}
                  onChange={(e) => updateVoteCampaign(ci, { title: e.target.value.trim() || undefined })}
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Closes at (local time)"
                  type="datetime-local"
                  value={isoToDatetimeLocalValue(camp.closesAt)}
                  onChange={(e) => updateVoteCampaign(ci, { closesAt: datetimeLocalToIso(e.target.value) })}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>
            </Paper>
          ))}
        </Stack>
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        <Typography component="h2" variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          Roadmap
        </Typography>
        <Button startIcon={<AddIcon />} onClick={addRoadmap} sx={{ mb: 2, textTransform: 'none' }}>
          Add roadmap item
        </Button>
        {payload.roadmap.length === 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            No roadmap items. Add items to show what is planned or in progress.
          </Alert>
        ) : null}
        <Stack spacing={2}>
          {payload.roadmap.map((item, mi) => (
            <Paper key={item.id} variant="outlined" sx={{ p: 2, borderRadius: '12px' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Item #{mi + 1}
                </Typography>
                <Box>
                  <IconButton
                    size="small"
                    onClick={() => moveRoadmap(mi, -1)}
                    aria-label={`Move roadmap item ${mi + 1} up in list`}
                  >
                    <KeyboardArrowUpIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => moveRoadmap(mi, 1)}
                    aria-label={`Move roadmap item ${mi + 1} down in list`}
                  >
                    <KeyboardArrowDownIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => removeRoadmap(mi)}
                    aria-label={`Remove roadmap item ${mi + 1}`}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Box>
              </Stack>
              <Stack spacing={1.5}>
                <TextField
                  size="small"
                  label="Id"
                  value={item.id}
                  onChange={(e) => updateRoadmap(mi, { id: e.target.value })}
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Title"
                  value={item.title}
                  onChange={(e) => updateRoadmap(mi, { title: e.target.value })}
                  fullWidth
                />
                <AdminProductMarkdownField
                  label="Summary"
                  value={item.summary}
                  onChange={(t) => updateRoadmap(mi, { summary: t })}
                  height={220}
                  helperText="Markdown; shown on the Roadmap tab in What’s new."
                  aria-label={`Roadmap item ${mi + 1} summary markdown`}
                />
                <TextField
                  select
                  size="small"
                  label="Status"
                  value={item.status}
                  onChange={(e) => updateRoadmap(mi, { status: e.target.value as RoadmapItemStatus })}
                  fullWidth
                >
                  {ROADMAP_STATUSES.map((s) => (
                    <MenuItem key={s} value={s}>
                      {formatRoadmapStatusLabel(s)}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  size="small"
                  label="Theme (optional)"
                  value={item.theme || ''}
                  onChange={(e) => updateRoadmap(mi, { theme: e.target.value || undefined })}
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Quarter (optional)"
                  value={item.quarter || ''}
                  onChange={(e) => updateRoadmap(mi, { quarter: e.target.value || undefined })}
                  fullWidth
                />
                <TextField
                  select
                  size="small"
                  label="Vote campaign (optional)"
                  value={item.voteCampaignId || ''}
                  onChange={(e) =>
                    updateRoadmap(mi, { voteCampaignId: e.target.value.trim() || undefined })
                  }
                  fullWidth
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {voteCampaigns.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.id}
                      {c.title ? ` — ${c.title}` : ''}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
            </Paper>
          ))}
        </Stack>
      </TabPanel>

      <TabPanel value={activeTab} index={3}>
        <Typography component="h2" variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          Vote results
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Live tallies from the database (same data as the daily digest email). Requires global admin session.
        </Typography>
        <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button
            variant="outlined"
            onClick={() => void loadVoteReport()}
            disabled={voteReportLoading}
            sx={{ textTransform: 'none' }}
          >
            {voteReportLoading ? 'Loading…' : 'Refresh vote results'}
          </Button>
          <Tooltip
            title={
              canPromoteTopVoted
                ? 'Appends a new planned roadmap row from the highest vote count (title prefixed “[Prioritized]”).'
                : 'Load results with at least one vote and matching roadmap row in this draft.'
            }
          >
            <span>
              <Button
                variant="contained"
                onClick={promoteTopVotedToNewRoadmapItem}
                disabled={!canPromoteTopVoted}
                sx={{ textTransform: 'none' }}
              >
                Add top-voted as new roadmap row
              </Button>
            </span>
          </Tooltip>
        </Stack>
        {promoteFeedback && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setPromoteFeedback(null)}>
            {promoteFeedback}
          </Alert>
        )}
        {voteReportError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setVoteReportError(null)}>
            {voteReportError}
          </Alert>
        )}
        {!voteReport && !voteReportLoading && !voteReportError ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            Load vote results to see tallies and recent vote rows from the database.
          </Alert>
        ) : null}
        {voteReport && (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: '12px', mb: 2 }}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Generated {new Date(voteReport.generatedAt).toLocaleString()} — payload version{' '}
              {voteReport.payloadVersion ?? '—'}
            </Typography>
            <TableContainer>
              <Typography
                component="p"
                variant="caption"
                id="product-updates-vote-summary-caption"
                sx={{ px: 0, py: 0.5, color: 'text.secondary' }}
              >
                Vote counts and status per roadmap item.
              </Typography>
              <Table
                size="small"
                aria-labelledby="product-updates-vote-summary-caption"
                aria-label="Roadmap vote summary"
              >
                <TableHead>
                  <TableRow>
                    <TableCell component="th" scope="col">
                      Item
                    </TableCell>
                    <TableCell component="th" scope="col">
                      Campaign
                    </TableCell>
                    <TableCell component="th" scope="col">
                      Status
                    </TableCell>
                    <TableCell component="th" scope="col" align="right">
                      Votes
                    </TableCell>
                    <TableCell component="th" scope="col">
                      Voting
                    </TableCell>
                    <TableCell component="th" scope="col">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {voteReport.items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography variant="body2" color="text.secondary">
                          No roadmap items with a vote campaign or recorded votes.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    voteReport.items.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {row.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {row.voteCampaignId || '—'}
                          {row.campaignTitle ? (
                            <Typography variant="caption" display="block" color="text.secondary">
                              {row.campaignTitle}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={formatRoadmapStatusLabel(row.status)}
                            color={roadmapStatusChipColor(row.status)}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right">{row.voteCount}</TableCell>
                        <TableCell>
                          {row.voteCampaignId ? (
                            <Chip
                              size="small"
                              label={row.votingOpen ? 'Open' : 'Closed'}
                              color={row.votingOpen ? 'success' : 'default'}
                              variant={row.votingOpen ? 'filled' : 'outlined'}
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Tooltip
                            title={
                              roadmapById.has(row.id)
                                ? 'Duplicate title, summary, and tags into a new planned row (no vote campaign).'
                                : 'This id is not in your current roadmap draft — reload after saving elsewhere.'
                            }
                          >
                            <span>
                              <Button
                                size="small"
                                variant="text"
                                disabled={!roadmapById.has(row.id)}
                                onClick={() => duplicateVoteReportRowToRoadmap(row.id)}
                                sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                              >
                                Copy to new row
                              </Button>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Button
              size="small"
              onClick={() => setVoteDetailOpen((o) => !o)}
              sx={{ mt: 2, textTransform: 'none' }}
              aria-expanded={voteDetailOpen}
              aria-controls={VOTE_DETAIL_REGION_ID}
              id={`${TAB_PREFIX}-vote-detail-toggle`}
            >
              {voteDetailOpen ? 'Hide' : 'Show'} recent vote rows ({voteReport.votes.length}
              {voteReport.voteRowsTruncated ? '+' : ''})
            </Button>
            <Collapse in={voteDetailOpen}>
              <Box id={VOTE_DETAIL_REGION_ID} role="region" aria-labelledby={`${TAB_PREFIX}-vote-detail-toggle`}>
                <Typography
                  component="p"
                  variant="caption"
                  id="product-updates-vote-rows-caption"
                  sx={{ mt: 1, color: 'text.secondary' }}
                >
                  Individual vote records (most recent first, may be truncated).
                </Typography>
                <TableContainer sx={{ mt: 0.5 }}>
                  <Table
                    size="small"
                    aria-labelledby="product-updates-vote-rows-caption"
                    aria-label="Recent roadmap votes"
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell component="th" scope="col">
                          Roadmap item id
                        </TableCell>
                        <TableCell component="th" scope="col">
                          User id
                        </TableCell>
                        <TableCell component="th" scope="col">
                          Campaign
                        </TableCell>
                        <TableCell component="th" scope="col">
                          Created
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {voteReport.votes.map((v, i) => (
                        <TableRow key={`${v.userId}-${v.roadmapItemId}-${i}`}>
                          <TableCell>{v.roadmapItemId}</TableCell>
                          <TableCell>{v.userId}</TableCell>
                          <TableCell>{v.voteCampaignId}</TableCell>
                          <TableCell>
                            {v.createdAt ? new Date(v.createdAt).toLocaleString() : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {voteReport.voteRowsTruncated && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    List truncated — oldest rows omitted.
                  </Typography>
                )}
              </Box>
            </Collapse>
          </Paper>
        )}
      </TabPanel>
    </Box>
  )
}
